<template>
  <IndicatorCard
    :loading="loading"
    :has-data="departs.length > 0"
    :is-result="isResult"
    :no-data-message="$t('Nenhum setor encontrado')"
    :title="title"
    :description="indicator.description"
    :excel-endpoint="indicator.excelEndpoint"
    :hide-excel="!isResult"
    :excel-loading="excelLoading"
    @export-excel="requestExcel"
  >
    <template #date-range>
      <DateRangePicker
        :start-date="startDate"
        :end-date="endDate"
        :disabled="isResult"
        @change="onDateRangeChange"
      />
    </template>

    <v-row class="ma-0" justify="center">
      <v-col cols="12">
        <strong>Total: {{ total }}</strong>

        <BaseEChart :option="chartOption" :loading="loading" height="320px" />

        <v-data-table
          :loading="loading"
          :loading-text="$t('Carregando...')"
          :no-data-text="$t('Nenhum setor encontrado')"
          :headers="headers"
          :items="departs"
          class="main-table"
          :hide-default-footer="!filterProp.data"
          hide-default-header
          dense
          :header-props="headerProps"
          :items-per-page="14"
          :mobile-breakpoint="0"
        >
          <template #header="{ props, on }">
            <thead>
              <tr>
                <th
                  class="text-center"
                  v-for="header in props.headers"
                  :key="header.value"
                >
                  <v-simple-checkbox
                    v-if="header.value === 'data-table-select'"
                    v-model="props.everyItem"
                    :indeterminate="props.someItems && !props.everyItem"
                    color="purple"
                    @input="on['toggle-select-all']"
                  ></v-simple-checkbox>

                  <div>
                    <v-hover v-slot="{ hover }">
                      <div>
                        <span>{{ $t(header.text) }}</span>
                        <v-icon
                          v-if="
                            hover &&
                            header.value != 'data-table-select' &&
                            header.value != 'actions'
                          "
                          color="white"
                          class="mx-2"
                          @click="on.sort(header.value)"
                        >
                          {{
                            props.options.sortBy.includes(header.value)
                              ? `mdi-chevron-down`
                              : `mdi-chevron-up`
                          }}
                        </v-icon>
                      </div>
                    </v-hover>
                  </div>
                </th>
              </tr>
            </thead>
          </template>

          <template #[`item.department`]="{ item }">
            {{ item.departmentName }}
          </template>

          <template #[`item.quantity`]="{ item }">
            {{ item.serviceOrdersQuantity }}
          </template>
        </v-data-table>

        <div class="pagination" v-if="!filterProp.data">
          <div class="item-pagination block-two">
            <v-pagination
              class="pagination-item"
              v-model="filter.page"
              :length="totalPages"
              :total-visible="9"
            />
          </div>
        </div>
      </v-col>
    </v-row>

    <template #review>
      <v-row class="ma-0">
        <v-col cols="12" v-if="isResult && review">
          <strong>{{ $t('Análise crítica') }}</strong>
          <p v-html="review"></p>
        </v-col>
        <v-col cols="12" v-else-if="!isResult">
          <tiptap-vuetify
            v-model="filter.review"
            :extensions="extensions"
            :label="$t('Análise crítica')"
            :placeholder="
              $t('Digite sua análise crítica para o indicador abaixo')
            "
            :card-props="{ flat: true, outlined: true, rounded: true }"
            class="primary-input"
            outlined
            dense
            hide-details="auto"
          />
        </v-col>
      </v-row>
    </template>
  </IndicatorCard>
</template>

<script>
import { getIndicatorById } from '../../../../../indicators/indicatorRegistry'
import ApiService from '../../../../../../../../services/ApiService'
import IndicatorCard from '../../shared/IndicatorCard.vue'
import BaseEChart from '../../shared/BaseEChart.vue'
import DateRangePicker from '../../shared/DateRangePicker.vue'
import { buildBarOption } from '../../shared/chartConfigs'
import { variedBarColors } from '../../shared/echartsTheme'
import moment from 'moment'
import {
  TiptapVuetify,
  Heading,
  Bold,
  Italic,
  Strike,
  Underline,
  Code,
  Paragraph,
  BulletList,
  OrderedList,
  ListItem,
  Link,
  Blockquote,
  HardBreak,
  HorizontalRule,
  History,
} from 'tiptap-vuetify'

export default {
  components: {
    IndicatorCard,
    BaseEChart,
    DateRangePicker,
    TiptapVuetify,
  },

  props: {
    title: { type: String, default: '' },
    apiEndpointOverride: { type: String, default: '' },
    filterProp: Object,
    institution: Object,
    unidades: Array,
    isResult: { type: Boolean, default: false },
    review: String,
  },

  async created() {
    this.filter.institutionId = this.institution.id
    this.filter.unidadeIds = this.unidades

    const lastMonthStart = moment()
      .add(-1, 'month')
      .clone()
      .startOf('month')
      .format('YYYY-MM-DD')
    const lastMonthEnd = moment()
      .add(-1, 'month')
      .clone()
      .endOf('month')
      .format('YYYY-MM-DD')

    this.filter.startDate = lastMonthStart
    this.filter.endDate = lastMonthEnd
    this.startDate = lastMonthStart
    this.endDate = lastMonthEnd

    if (this.filterProp && this.filterProp.institutionId) {
      this.filter = this.filterProp
      this.startDate = moment(this.filter.startDate)
        .clone()
        .startOf('month')
        .format('YYYY-MM-DD')
      this.endDate = moment(this.filter.endDate)
        .clone()
        .endOf('month')
        .format('YYYY-MM-DD')
    }

    await this.list()
  },

  data: () => ({
    excelLoading: false,
    extensions: [
      History, Blockquote, Link, Underline, Strike, Italic, ListItem,
      BulletList, OrderedList,
      [Heading, { options: { levels: [1, 2, 3] } }],
      Bold, Code, HorizontalRule, Paragraph, HardBreak,
    ],
    totalPages: 1,
    loading: true,
    departmentNames: [],
    requestQuantity: [],
    departs: [],
    filter: {
      page: 1,
      pageSize: 14,
      review: '',
      startDate: null,
      endDate: null,
    },
    startDate: null,
    endDate: null,
    headers: [
      { text: '', align: 'left', value: '' },
      { text: 'Setor', align: 'left', value: 'department' },
      { text: 'Quantidade', align: 'center', value: 'quantity' },
    ],
    headerProps: { sortByText: 'Ordenar por' },
    apiService: new ApiService(),
    total: 0,
  }),

  computed: {
    indicator() {
      return getIndicatorById('request_by_sector') || {}
    },
    chartOption() {
      return buildBarOption({
        categories: this.departmentNames,
        series: [
          {
            name: this.$t('Solicitações'),
            data: this.requestQuantity,
            colors: variedBarColors,
          },
        ],
        showLegend: false,
      })
    },
  },

  methods: {
    requestExcel() {
      if (!this.indicator.excelEndpoint) return
      this.excelLoading = true
      const filename = `${this.indicator.legacyKey}.xlsx`
      this.apiService
        .post(this.indicator.excelEndpoint, { ...this.filter, pageSize: 999999 })
        .then((response) => {
          const data = `data:application/octet-stream;base64,${response.content}`
          const link = document.createElement('a')
          link.href = data
          link.setAttribute('download', filename)
          document.body.appendChild(link)
          link.click()
          document.body.removeChild(link)
        })
        .finally(() => {
          this.excelLoading = false
        })
    },

    setDates() {
      this.filter.startDate = moment(this.startDate)
        .clone()
        .startOf('month')
        .format('YYYY-MM-DD')

      this.filter.endDate = moment(this.endDate)
        .clone()
        .endOf('month')
        .format('YYYY-MM-DD')
    },

    onDateRangeChange({ startDate, endDate }) {
      this.startDate = startDate
      this.endDate = endDate
      this.filterProp.data = null
      this.list()
    },

    setInfos() {
      const model = {
        chart: getIndicatorById('request_by_sector').legacyKey,
        ...this.filter,
        data: null,
      }
      this.$emit('setInfos', model)
    },

    extractChartData(response) {
      this.total = response.content.total
      this.totalPages = response.content.pager.totalPages
      this.departs = response.content.objects

      this.departmentNames = []
      this.requestQuantity = []

      if (response.content.chartsModel?.length) {
        response.content.chartsModel.forEach((chart) => {
          this.departmentNames.push(chart.name)
          this.requestQuantity.push(chart.quantity)
        })
      }
    },

    async list() {
      this.loading = true
      try {
        if (this.filterProp.data) {
          this.extractChartData(JSON.parse(this.filterProp.data))
          return
        }

        if (!this.isResult) this.setDates()
        this.filter.institutionId = this.institution.id
        this.filter.pageSize = 7
        this.filter.unidadeIds = this.unidades

        const response = await this.apiService.post(
          this.apiEndpointOverride || 'reports/requestByDepartment',
          this.filter
        )
        this.extractChartData(response)
      } finally {
        this.loading = false
      }
    },
  },

  watch: {
    'filter.page': {
      deep: true,
      handler() {
        this.list()
      },
    },
    filter: {
      deep: true,
      handler() {
        this.setInfos()
      },
    },
  },
}
</script>
